import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { ntfyService } from '../services/NtfyService';
import { getPreferences, updatePreferenceDirect } from '../hooks/usePreferences';

// Types matching server/src/types.ts SignalPrediction
export type MarketStatus = 'open' | 'closed' | 'pre-market' | 'post-market';

export interface LiveCandleOHLC {
  open: number;
  high: number;
  low: number;
  close: number;
  isGreen: boolean;
}

export interface SignalPrediction {
  probability: number;
  trend: 'rising' | 'falling' | 'stable';
  direction: 'LONG' | 'SHORT' | null;
  wickPct: number;
  candleLength: number;
  isQualifying: boolean;
  atr: number;
  atrMinThreshold: number;
  isLowATR: boolean;
  marketStatus: MarketStatus;
  marketMessage?: string;
  liveCandle?: LiveCandleOHLC;
  elapsedSeconds: number;
  secondsToEvaluation: number;
  candleStartTime: number;
  willBeSkipped: boolean;
  skipReason: 'HOD' | 'LOD' | 'LOW_ATR' | null;
  skipDistance: number;
  alertTriggered: boolean;
  alertLevel: 'none' | 'watch' | 'ready' | 'imminent';
  // Info overlay fields (for real-time current candle analysis)
  wickThresholdMin?: number;
  wickThresholdMax?: number;
  minCandleLengthPoints?: number;
  passesLengthFilter?: boolean;
  passesAtrFilter?: boolean;
  distanceFromHOD?: number;
  distanceFromLOD?: number;
  hodLodRangePoints?: number;
  passesHodFilter?: boolean;
  passesLodFilter?: boolean;
  sessionHigh?: number;
  sessionLow?: number;
  lastCandleResult?: {
    wasSignal: boolean;
    direction: 'LONG' | 'SHORT' | null;
    wasSkipped: boolean;
    skipReason: 'HOD' | 'LOD' | 'LOW_ATR' | null;
    wickPct?: number;
    thresholdMax?: number;
    alertWasSent?: boolean;
    atr?: number;
  };
  // Confidence scoring for live candle
  confidence?: number;
  confidenceFactors?: Array<{
    name: string;
    price: number;
    positionPct: number;
    zone: 'low' | 'mid' | 'high';
    contribution: number;
    weight: number;
  }>;
  emaStackAligned?: boolean;
  vwapAligned?: boolean;
  levelDensity?: number;
  nextLevelDistance?: number;
  // Indicator values
  ema9?: number;
  ema21?: number;
  ema200?: number;
  vwap?: number;
}

export interface PredictorSettings {
  enabled: boolean;
  alertThreshold: number;
  startTrackingAt: number;
  // Display options (matching Android)
  showPercentSymbol: boolean;
  showTrendArrow: boolean;
  showWickPct: boolean;
  showTimeLeft: boolean;
  showAlertStatus: boolean;
  compactMode: boolean;
  // Appearance
  opacity: number;
  theme: 'default' | 'dark' | 'neon' | 'minimal';
  accentColor: 'green' | 'blue' | 'purple' | 'orange';
  // Ntfy notification settings
  ntfyEnabled: boolean;
  ntfyTopic: string;
}

export interface PredictorStats {
  totalAlerts: number;
  signals: number;
  skipped: number;
  falsePositives: number;
}

// Helper to normalize symbol for cache keys (strip exchange suffix and month code)
const normalizeSymbolForCache = (symbol: string): string => {
  if (!symbol) return '';
  // Strip exchange suffix (.CME, etc) and normalize to base (MNQ, MES, NQ, ES)
  return symbol
    .replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '')
    .replace(/[A-Z]\d{1,2}$/i, '') // Remove month/year code like H6, Z5
    .toUpperCase();
};

interface SignalPredictorState {
  // Per-symbol predictions (keyed by normalized symbol like "MNQ", "MES")
  predictions: Map<string, SignalPrediction>;
  // Legacy single prediction (for backward compatibility with components not yet updated)
  prediction: SignalPrediction | null;

  // Settings
  settings: PredictorSettings;

  // Stats
  stats: PredictorStats;

  // UI state
  isMinimized: boolean;
  showCandle: boolean;
  forceLive: boolean;
  showStats: boolean;
  position: { x: number; y: number };

  // Actions
  setPrediction: (prediction: SignalPrediction & { symbol?: string }) => void;
  getPredictionForSymbol: (symbol: string) => SignalPrediction | null;
  updateSettings: (settings: Partial<PredictorSettings>) => void;
  updateStats: (stats: Partial<PredictorStats>) => void;
  incrementStat: (stat: keyof PredictorStats) => void;
  setMinimized: (minimized: boolean) => void;
  setShowCandle: (show: boolean) => void;
  setForceLive: (force: boolean) => void;
  setShowStats: (show: boolean) => void;
  setPosition: (position: { x: number; y: number }) => void;
  resetStats: () => void;
  syncFromPreferences: () => void;
}

const DEFAULT_SETTINGS: PredictorSettings = {
  enabled: true,
  alertThreshold: 70,
  startTrackingAt: 100,
  showPercentSymbol: true,
  showTrendArrow: true,
  showWickPct: true,
  showTimeLeft: true,
  showAlertStatus: true,
  compactMode: false,
  opacity: 80, // 80% opacity = 20% transparent
  theme: 'dark',
  accentColor: 'green',
  ntfyEnabled: false,
  ntfyTopic: '',
};

const DEFAULT_STATS: PredictorStats = {
  totalAlerts: 0,
  signals: 0,
  skipped: 0,
  falsePositives: 0,
};

// Valid theme values for type validation
const VALID_THEMES = ['default', 'dark', 'neon', 'minimal'] as const;
const isValidTheme = (theme: unknown): theme is PredictorSettings['theme'] =>
  typeof theme === 'string' && VALID_THEMES.includes(theme as any);

// Load settings from server preferences
const loadSettings = (): PredictorSettings => {
  const prefs = getPreferences();
  const saved = prefs.signalPredictor?.settings;
  if (saved) {
    // Validate theme value
    const validatedSettings = {
      ...saved,
      theme: isValidTheme(saved.theme) ? saved.theme : DEFAULT_SETTINGS.theme,
    };
    return { ...DEFAULT_SETTINGS, ...validatedSettings };
  }
  return DEFAULT_SETTINGS;
};

// Load stats from server preferences
const loadStats = (): PredictorStats => {
  const prefs = getPreferences();
  const saved = prefs.signalPredictor?.stats;
  if (saved) {
    return { ...DEFAULT_STATS, ...saved };
  }
  return DEFAULT_STATS;
};

// Load UI state from server preferences
const loadUIState = () => {
  const prefs = getPreferences();
  const sp = prefs.signalPredictor;
  return {
    isMinimized: sp?.isMinimized ?? false,
    showCandle: sp?.showCandle ?? true,
    showStats: sp?.showStats ?? false,
    position: sp?.position ?? { x: window.innerWidth - 320, y: 80 },
  };
};

export const useSignalPredictorStore = create<SignalPredictorState>()(subscribeWithSelector((set, get) => {
  const uiState = loadUIState();

  return {
    predictions: new Map<string, SignalPrediction>(),
    prediction: null,
    settings: loadSettings(),
    stats: loadStats(),
    isMinimized: uiState.isMinimized,
    showCandle: uiState.showCandle,
    forceLive: false,
    showStats: uiState.showStats,
    position: uiState.position,

    setPrediction: (prediction) => {
      const state = get();
      const symbol = (prediction as any).symbol || '';
      const normalizedSymbol = normalizeSymbolForCache(symbol);

      // Get previous prediction for this symbol (for stats tracking)
      const prevPrediction = normalizedSymbol
        ? state.predictions.get(normalizedSymbol)
        : state.prediction;

      // Update stats and send notification when alert triggers
      if (prediction.alertTriggered && (!prevPrediction || !prevPrediction.alertTriggered)) {
        set((s) => ({
          stats: {
            ...s.stats,
            totalAlerts: s.stats.totalAlerts + 1,
          },
        }));

        // Send ntfy notification if enabled
        if (state.settings.ntfyEnabled && state.settings.ntfyTopic) {
          ntfyService.sendAlert(prediction);
        }
      }

      // Update stats when signal forms
      if (prediction.isQualifying && prevPrediction && !prevPrediction.isQualifying) {
        set((s) => ({
          stats: {
            ...s.stats,
            signals: s.stats.signals + 1,
          },
        }));
      }

      // Update stats when skipped
      if (prediction.willBeSkipped && prevPrediction && !prevPrediction.willBeSkipped) {
        set((s) => ({
          stats: {
            ...s.stats,
            skipped: s.stats.skipped + 1,
          },
        }));
      }

      // Store in per-symbol map
      if (normalizedSymbol) {
        const newPredictions = new Map(state.predictions);
        newPredictions.set(normalizedSymbol, prediction);
        set({ predictions: newPredictions, prediction }); // Also update legacy single prediction
      } else {
        set({ prediction });
      }
    },

    getPredictionForSymbol: (symbol: string) => {
      const state = get();
      const normalizedSymbol = normalizeSymbolForCache(symbol);
      if (!normalizedSymbol) return state.prediction;
      return state.predictions.get(normalizedSymbol) || null;
    },

    updateSettings: (newSettings) => {
      set((state) => {
        const settings = { ...state.settings, ...newSettings };
        updatePreferenceDirect('signalPredictor', { settings });

        // Update NtfyService config if ntfy settings changed
        if ('ntfyEnabled' in newSettings || 'ntfyTopic' in newSettings) {
          ntfyService.updateConfig({
            enabled: settings.ntfyEnabled,
            topic: settings.ntfyTopic,
          });
        }

        return { settings };
      });
    },

    updateStats: (newStats) => {
      set((state) => {
        const stats = { ...state.stats, ...newStats };
        updatePreferenceDirect('signalPredictor', { stats });
        return { stats };
      });
    },

    incrementStat: (stat) => {
      set((state) => {
        const stats = { ...state.stats, [stat]: state.stats[stat] + 1 };
        updatePreferenceDirect('signalPredictor', { stats });
        return { stats };
      });
    },

    setMinimized: (minimized) => {
      console.log('[SignalPredictor] setMinimized called with:', minimized);
      updatePreferenceDirect('signalPredictor', { isMinimized: minimized });
      set({ isMinimized: minimized });
    },

    setShowCandle: (show) => {
      updatePreferenceDirect('signalPredictor', { showCandle: show });
      set({ showCandle: show });
    },

    setForceLive: (force) => {
      set({ forceLive: force });
    },

    setShowStats: (show) => {
      updatePreferenceDirect('signalPredictor', { showStats: show });
      set({ showStats: show });
    },

    setPosition: (position) => {
      updatePreferenceDirect('signalPredictor', { position });
      set({ position });
    },

    resetStats: () => {
      updatePreferenceDirect('signalPredictor', { stats: DEFAULT_STATS });
      set({ stats: DEFAULT_STATS });
    },

    syncFromPreferences: () => {
      const prefs = getPreferences();
      const sp = prefs.signalPredictor;
      console.log('[SignalPredictor] syncFromPreferences called, sp:', sp);
      if (sp) {
        console.log('[SignalPredictor] Setting isMinimized to:', sp.isMinimized ?? false);
        set({
          isMinimized: sp.isMinimized ?? false,
          showCandle: sp.showCandle ?? true,
          showStats: sp.showStats ?? false,
          position: sp.position ?? { x: window.innerWidth - 320, y: 80 },
          settings: sp.settings ? {
            ...DEFAULT_SETTINGS,
            ...sp.settings,
            theme: isValidTheme(sp.settings.theme) ? sp.settings.theme : DEFAULT_SETTINGS.theme,
          } : DEFAULT_SETTINGS,
          stats: sp.stats ? { ...DEFAULT_STATS, ...sp.stats } : DEFAULT_STATS,
        });
      } else {
        console.log('[SignalPredictor] No signalPredictor preferences found');
      }
    },
  };
}));

